/**
 * Homepage GSAP + ScrollTrigger animations (index template only).
 * Sense reveal-on-scroll is disabled so these do not double-animate.
 *
 * Section DOM ids look like: shopify-section-template--XXXX__slideshow_x893tW
 * so lookups match by section key suffix, not a bare id.
 */
(function () {
  'use strict';

  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

  gsap.registerPlugin(ScrollTrigger);

  var ACCENT_COLOR = '#008080';

  /* Keys match templates/index.json section keys */
  var SECTION_KEYS = {
    slideshow: 'slideshow_x893tW',
    flashSale: '17859992102fe05b8b',
    featuredA: 'featured-collection',
    featuredB: 'featured_collection_WamwCa',
    richText: 'rich_text_NdFKdb',
    collectionList: 'collection_list_ybJEeR',
    discover: '1786010046ff141b25',
    shopByVideo: 'shop_by_video_home',
    communitySignup: 'community_signup',
    sourcingJourney: 'sourcing_journey',
  };

  var ACCENT_WORD = {
    slideshow_x893tW: 'Hing',
    'featured-collection': 'Arrivals',
    featured_collection_WamwCa: 'Drops',
    '17859992102fe05b8b': 'Hing',
    rich_text_NdFKdb: 'Compare',
    collection_list_ybJEeR: 'Collection',
    '1786010046ff141b25': 'Collection',
    shop_by_video_home: 'Craft',
    community_signup: 'Spice',
    sourcing_journey: 'Kitchen',
  };

  var ATTR_INIT = 'data-gsap-homepage-init';
  var ATTR_SLIDE_LISTENER = 'data-gsap-slide-listener';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isDesignMode() {
    return Boolean(window.Shopify && Shopify.designMode);
  }

  /** Normalize shopify-section-… id → JSON section key */
  function sectionKeyFromId(id) {
    if (!id) return '';
    return String(id)
      .replace(/^shopify-section-/, '')
      .replace(/^template--\d+__/, '')
      .replace(/^sections--\d+__/, '');
  }

  function getSection(key, root) {
    var scope = root && root.nodeType === 1 ? root : document;
    var want = sectionKeyFromId(key) || key;

    if (scope.id && scope.id.indexOf('shopify-section-') === 0) {
      if (sectionKeyFromId(scope.id) === want) return scope;
    }

    var nodes =
      scope === document
        ? document.querySelectorAll('[id^="shopify-section-"]')
        : scope.querySelectorAll
          ? scope.querySelectorAll('[id^="shopify-section-"]')
          : [];

    for (var i = 0; i < nodes.length; i++) {
      if (sectionKeyFromId(nodes[i].id) === want) return nodes[i];
    }

    /* Exact legacy id fallback */
    var exact = document.getElementById('shopify-section-' + want);
    if (exact) return exact;

    return null;
  }

  function accentWordForSection(section) {
    if (!section) return null;
    return ACCENT_WORD[sectionKeyFromId(section.id)] || null;
  }

  function collectAnimatable(section) {
    if (!section) return [];
    return section.querySelectorAll(
      [
        '.banner__heading',
        '.banner__text',
        '.banner__buttons .button',
        '.slideshow__media img',
        '.collection__title .title',
        '.clearance-deals__title',
        '.rich-text__heading',
        '.rich-text__text',
        '.rich-text__divider',
        '.rich-text__trust-badge',
        '.rich-text__trust-item',
        '.rich-text__eyebrow',
        '.rich-text__cta',
        '.rich-text__spoon-decor',
        '.collection-list-title',
        '.shop-by-video__heading',
        '.yh-sourcing__eyebrow',
        '.yh-sourcing__heading',
        '.yh-sourcing__subheading',
        '.yh-community__eyebrow',
        '.yh-community__heading',
        '.yh-community__subheading',
        '.yh-community__field',
        '.yh-community__submit',
        '[class*="flash-hero__badge-"]',
        '[class*="flash-hero__heading-"]',
        '[class*="flash-hero__subtext-"]',
        '[class*="flash-hero__timer-"]',
        '[class*="flash-hero__button-"]',
        '[class*="flash-hero__trust-"]',
        '[class*="ai-itc__content"]',
        '[class*="ai-itc__slideshow"]',
        '[class*="ai-itc__container"]',
        '.yh-heading-accent',
      ].join(',')
    );
  }

  /* ─── Heading accent (colour + native underline) ─── */

  function wrapAccentWord(headingEl, word) {
    /* Heading word highlight disabled sitewide */
    return null;
  }

  function isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function accentColorFor(span) {
    return '';
  }

  function prepareAccent(span) {
    if (!span) return;
    span.classList.remove('is-lit');
    gsap.set(span, { clearProps: 'color' });
  }

  function finalizeAccent(span) {
    if (!span) return;
    span.classList.remove('is-lit');
    gsap.set(span, { clearProps: 'color' });
  }

  function buildAccentTimeline(span) {
    var tl = gsap.timeline();
    if (!span) return tl;

    var color = accentColorFor(span);
    prepareAccent(span);

    if (!color) {
      /* Keep solid heading color on mobile hero */
      gsap.set(span, { clearProps: 'color' });
      return tl;
    }

    tl.to(
      span,
      {
        color: color,
        duration: 0.45,
        ease: 'power2.out',
        onStart: function () {
          span.classList.add('is-lit');
        },
      },
      0
    );
    return tl;
  }

  function showFinalState(section) {
    var els = collectAnimatable(section);
    if (els.length) {
      gsap.set(els, { clearProps: 'opacity,transform,visibility' });
      gsap.set(els, { opacity: 1, y: 0, scale: 1, visibility: 'visible' });
    }
    section.querySelectorAll('.yh-heading-accent').forEach(finalizeAccent);
  }

  function revealIfInView(el) {
    if (!el) return;
    var op = parseFloat(window.getComputedStyle(el).opacity);
    if (!(op < 0.05)) return;
    var rect = el.getBoundingClientRect();
    var vh = window.innerHeight || 0;
    if (rect.bottom < -80 || rect.top > vh * 1.2) return;
    gsap.killTweensOf(el);
    gsap.set(el, { opacity: 1, y: 0, scale: 1, visibility: 'visible' });
  }

  function revealStuckInView() {
    Object.keys(SECTION_KEYS).forEach(function (name) {
      var section = getSection(SECTION_KEYS[name], document);
      if (!section) return;
      collectAnimatable(section).forEach(revealIfInView);
    });
  }

  function refreshAndUnstick() {
    ScrollTrigger.refresh();
    revealStuckInView();
  }

  function killSectionAnimations(section) {
    if (!section) return;

    ScrollTrigger.getAll().forEach(function (st) {
      var trigger = st.trigger;
      if (!trigger) return;
      if (trigger === section || section.contains(trigger)) {
        st.kill();
      }
    });

    gsap.killTweensOf(collectAnimatable(section));

    var slideshow = section.querySelector('slideshow-component');
    if (slideshow) slideshow.removeAttribute(ATTR_SLIDE_LISTENER);

    section.removeAttribute(ATTR_INIT);
  }

  function markInit(section) {
    if (!section) return false;
    if (section.getAttribute(ATTR_INIT) === 'true') return false;
    section.setAttribute(ATTR_INIT, 'true');
    return true;
  }

  var HEADING_SEL =
    '.banner__heading, .collection__title .title, .rich-text__heading, .collection-list-title, .shop-by-video__heading, .yh-community__heading, .yh-sourcing__heading, [class*="flash-hero__heading-"], [class*="ai-itc__heading"]';

  /* ─── Hero / Slideshow ─── */

  function getActiveSlide(section) {
    return (
      section.querySelector('.slideshow__slide.is-active') ||
      section.querySelector('.slideshow__slide[aria-hidden="false"]') ||
      section.querySelector('.slideshow__slide')
    );
  }

  function setActiveSlideClass(section, activeSlide) {
    section.querySelectorAll('.slideshow__slide').forEach(function (slide) {
      slide.classList.toggle('is-active', slide === activeSlide);
    });
  }

  function animateHeroSlide(slide, section) {
    if (!slide || prefersReducedMotion() || isDesignMode()) return;

    var heading = slide.querySelector('.banner__heading');
    var subheading = slide.querySelector('.banner__text');
    var ctas = gsap.utils.toArray(slide.querySelectorAll('.banner__buttons .button'));
    var media = slide.querySelector('.slideshow__media video, .slideshow__media img');
    var textTargets = [heading, subheading].concat(ctas).filter(Boolean);
    var word = accentWordForSection(section);
    var accent = heading ? wrapAccentWord(heading, word) : null;

    gsap.killTweensOf(textTargets);
    if (media) gsap.killTweensOf(media);
    if (accent) {
      gsap.killTweensOf(accent);
      finalizeAccent(accent);
    }

    if (heading) gsap.set(heading, { opacity: 0, y: 30 });
    if (subheading) gsap.set(subheading, { opacity: 0, y: 30 });
    if (ctas.length) gsap.set(ctas, { opacity: 0, scale: 0.95 });

    var tl = gsap.timeline();

    if (heading) {
      tl.to(heading, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' }, 0);
    }
    if (subheading) {
      tl.to(subheading, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' }, 0.15);
    }
    if (ctas.length) {
      tl.to(ctas, { opacity: 1, scale: 1, duration: 0.7, stagger: 0.1, ease: 'power2.out' }, 0.3);
    }

    if (media) {
      gsap.set(media, { scale: 1, transformOrigin: 'center center' });
      gsap.to(media, {
        scale: 1.05,
        duration: 8,
        ease: 'none',
        yoyo: true,
        repeat: -1,
      });
    }
  }

  function initHero(root) {
    var section = getSection(SECTION_KEYS.slideshow, root);
    if (!section || !markInit(section)) return;

    var word = accentWordForSection(section);
    section.querySelectorAll('.banner__heading').forEach(function (h) {
      wrapAccentWord(h, word);
    });

    if (prefersReducedMotion() || isDesignMode()) {
      showFinalState(section);
      return;
    }

    var slideshow = section.querySelector('slideshow-component');
    var active = getActiveSlide(section);
    if (active) {
      setActiveSlideClass(section, active);
      animateHeroSlide(active, section);
    }

    if (slideshow && slideshow.getAttribute(ATTR_SLIDE_LISTENER) !== 'true') {
      slideshow.setAttribute(ATTR_SLIDE_LISTENER, 'true');
      slideshow.addEventListener('slideChanged', function (event) {
        var current = event.detail && event.detail.currentElement;
        if (!current || !section.contains(current)) return;
        setActiveSlideClass(section, current);
        animateHeroSlide(current, section);
      });
    }
  }

  /* ─── Flash sale ─── */

  function initFlashSale(root) {
    var section = getSection(SECTION_KEYS.flashSale, root);
    if (!section || !markInit(section)) return;

    var heading = section.querySelector('[class*="flash-hero__heading-"]');
    var accent = section.querySelector('[class*="flash-hero__accent-"]');

    if (prefersReducedMotion() || isDesignMode()) {
      showFinalState(section);
      return;
    }

    var label = section.querySelector('[class*="flash-hero__badge-"]');
    var subtext = section.querySelector('[class*="flash-hero__subtext-"]');
    var timer = section.querySelector('[class*="flash-hero__timer-"]');
    var button = section.querySelector('[class*="flash-hero__button-"]');
    var trust = section.querySelector('[class*="flash-hero__trust-"]');
    var intro = [label, heading, subtext].filter(Boolean);

    if (intro.length) gsap.set(intro, { opacity: 0, y: 20 });
    if (timer) gsap.set(timer, { opacity: 0, scale: 0.9 });
    if (button) gsap.set(button, { opacity: 0 });
    if (trust) gsap.set(trust, { opacity: 0, y: 16 });
    if (accent) {
      accent.classList.add('yh-heading-accent');
      prepareAccent(accent);
    }

    var triggerEl = intro[0] || timer || button || section;
    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: triggerEl,
        start: 'top 90%',
        once: true,
        invalidateOnRefresh: true,
      },
    });

    if (intro.length) {
      tl.to(intro, { opacity: 1, y: 0, duration: 0.6, stagger: 0.1, ease: 'power2.out' }, 0);
    }
    if (accent) {
      tl.add(buildAccentTimeline(accent), 0.45);
    }
    if (timer) {
      tl.to(timer, { opacity: 1, scale: 1, duration: 0.6, ease: 'back.out(1.4)' }, '+=0.15');
    }
    if (button) {
      tl.to(button, { opacity: 1, duration: 0.5, ease: 'power2.out' }, '+=0.1');
    }
    if (trust) {
      tl.to(trust, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, '-=0.1');
    }
  }

  /* ─── Featured collections ─── */

  function initFeaturedCollection(sectionKey, root) {
    var section = getSection(sectionKey, root);
    if (!section || !markInit(section)) return;

    var title =
      section.querySelector('.clearance-deals__title') ||
      section.querySelector('.collection__title .title');
    var accent = wrapAccentWord(title, accentWordForSection(section));
    var cards = gsap.utils.toArray(
      section.querySelectorAll('li.grid__item, li.clearance-deals__slide, .clearance-deals__slide')
    );

    /*
      Never animate product card opacity — same blank-area bug as reels/sourcing
      when ScrollTrigger.batch misses (or Dawn scroll-trigger stays offscreen).
    */
    if (cards.length) {
      gsap.killTweensOf(cards);
      cards.forEach(function (card) {
        card.classList.add('scroll-trigger--cancel');
        card.classList.remove('scroll-trigger--offscreen');
      });
      gsap.set(cards, { clearProps: 'opacity,transform,visibility' });
      gsap.set(cards, { opacity: 1, y: 0, visibility: 'visible' });
    }

    if (prefersReducedMotion() || isDesignMode()) {
      showFinalState(section);
      if (accent) finalizeAccent(accent);
      return;
    }

    if (title) {
      gsap.set(title, { opacity: 0, y: 20 });
      if (accent) prepareAccent(accent);

      var titleTl = gsap.timeline({
        scrollTrigger: {
          trigger: title,
          start: 'top 90%',
          once: true,
          invalidateOnRefresh: true,
        },
      });
      titleTl.to(title, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, 0);
      if (accent) titleTl.add(buildAccentTimeline(accent), 0.35);
    }
  }

  /* ─── Rich text ─── */

  function initRichText(root) {
    var section = getSection(SECTION_KEYS.richText, root);
    if (!section || !markInit(section)) return;

    var heading = section.querySelector('.rich-text__heading');
    var text = section.querySelector('.rich-text__text');
    var divider = section.querySelector('.rich-text__divider');
    var badges = gsap.utils.toArray(section.querySelectorAll('.rich-text__trust-item, .rich-text__trust-badge'));
    var cta = section.querySelector('.rich-text__cta');
    var spoon = section.querySelector('.rich-text__spoon-decor');
    var eyebrow = section.querySelector('.rich-text__eyebrow');
    var accent = wrapAccentWord(heading, accentWordForSection(section));

    if (prefersReducedMotion() || isDesignMode()) {
      showFinalState(section);
      return;
    }

    if (spoon) gsap.set(spoon, { opacity: 0 });
    if (eyebrow) gsap.set(eyebrow, { opacity: 0, y: 12 });
    if (heading) gsap.set(heading, { opacity: 0, y: 20 });
    if (divider) gsap.set(divider, { opacity: 0, y: 12 });
    if (text) gsap.set(text, { opacity: 0 });
    if (badges.length) gsap.set(badges, { opacity: 0, y: 16 });
    if (cta) gsap.set(cta, { opacity: 0, y: 16 });
    if (accent) prepareAccent(accent);

    var triggerEl = heading || text || section;
    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: triggerEl,
        start: 'top 90%',
        once: true,
        invalidateOnRefresh: true,
      },
    });

    if (heading) {
      tl.to(heading, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, 0);
    }
    if (eyebrow) {
      tl.to(eyebrow, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 0);
    }
    if (spoon) {
      tl.to(spoon, { opacity: 1, duration: 0.65, ease: 'power2.out' }, 0);
    }
    if (accent) {
      tl.add(buildAccentTimeline(accent), 0.35);
    }
    if (divider) {
      tl.to(divider, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 0.15);
    }
    if (text) {
      tl.to(text, { opacity: 1, duration: 0.5, ease: 'power2.out' }, 0.22);
    }
    if (badges.length) {
      tl.to(
        badges,
        { opacity: 1, y: 0, duration: 0.45, stagger: 0.08, ease: 'power2.out' },
        0.35
      );
    }
    if (cta) {
      tl.to(cta, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 0.5);
    }
  }

  /* ─── Collection list ─── */

  function initCollectionList(root) {
    var section = getSection(SECTION_KEYS.collectionList, root);
    if (!section || !markInit(section)) return;

    var title = section.querySelector('.collection-list-title');
    var eyebrow = section.querySelector('.yh-collection-list__eyebrow');
    var divider = section.querySelector('.yh-collection-list__divider');
    var accent = wrapAccentWord(title, accentWordForSection(section) || 'Collection');
    var cards = gsap.utils.toArray(
      section.querySelectorAll('li.collection-list__item, .yh-coll-card')
    );

    /* Never hide collection cards — same blank-load bug as reels */
    if (cards.length) {
      gsap.killTweensOf(cards);
      cards.forEach(function (card) {
        card.classList.add('scroll-trigger--cancel');
        card.classList.remove('scroll-trigger--offscreen');
      });
      gsap.set(cards, { clearProps: 'opacity,transform,visibility' });
      gsap.set(cards, { opacity: 1, y: 0, visibility: 'visible' });
    }

    if (prefersReducedMotion() || isDesignMode()) {
      showFinalState(section);
      if (title) gsap.set(title, { clearProps: 'opacity,transform' });
      if (accent) finalizeAccent(accent);
      return;
    }

    if (title) {
      if (eyebrow) gsap.set(eyebrow, { opacity: 0, y: 12 });
      if (divider) gsap.set(divider, { opacity: 0 });
      gsap.set(title, { opacity: 0, y: 20 });
      if (accent) prepareAccent(accent);

      var titleTl = gsap.timeline({
        scrollTrigger: {
          trigger: title || section,
          start: 'top 90%',
          once: true,
          invalidateOnRefresh: true,
        },
      });
      if (eyebrow) titleTl.to(eyebrow, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' }, 0);
      titleTl.to(title, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, 0.05);
      if (accent) {
        titleTl.add(buildAccentTimeline(accent), 0.35);
        titleTl.eventCallback('onComplete', function () {
          finalizeAccent(accent);
        });
      }
      if (divider) titleTl.to(divider, { opacity: 1, duration: 0.4, ease: 'power2.out' }, 0.25);
    } else if (accent) {
      finalizeAccent(accent);
    }
  }

  /* ─── Discover carousel ─── */

  function initDiscover(root) {
    var section = getSection(SECTION_KEYS.discover, root);
    if (!section || !markInit(section)) return;

    var heading = section.querySelector('[class*="ai-itc__heading"]');
    var accent = wrapAccentWord(heading, accentWordForSection(section));

    if (prefersReducedMotion() || isDesignMode()) {
      showFinalState(section);
      return;
    }

    var container = section.querySelector('[class*="ai-itc__container"]');
    var content = section.querySelector('[class*="ai-itc__content"]');
    var slideshow = section.querySelector('[class*="ai-itc__slideshow"]');
    var targets = container ? [container] : [content, slideshow].filter(Boolean);

    if (accent) prepareAccent(accent);

    if (targets.length) {
      gsap.set(targets, { opacity: 0 });
      var tl = gsap.timeline({
        scrollTrigger: {
          trigger: targets[0],
          start: 'top 90%',
          once: true,
          invalidateOnRefresh: true,
        },
      });
      tl.to(targets, { opacity: 1, duration: 0.6, ease: 'power2.out' }, 0);
      if (accent) tl.add(buildAccentTimeline(accent), 0.35);
    } else if (accent) {
      prepareAccent(accent);
      ScrollTrigger.create({
        trigger: heading || section,
        start: 'top 90%',
        once: true,
        invalidateOnRefresh: true,
        onEnter: function () {
          buildAccentTimeline(accent);
        },
      });
    }
  }

  /* ─── Shop by Video ─── */

  function initShopByVideo(root) {
    var section = getSection(SECTION_KEYS.shopByVideo, root);
    if (!section || !markInit(section)) return;

    var heading = section.querySelector('.shop-by-video__heading');
    var divider = section.querySelector('.shop-by-video__divider');
    var accent = wrapAccentWord(heading, accentWordForSection(section));
    var cards = gsap.utils.toArray(section.querySelectorAll('.shop-by-video__card'));

    /*
      Never animate reel card opacity. ScrollTrigger.batch / fromTo can miss
      (theme editor, late layout, sticky CTA refresh) and leave cards at
      opacity:0 — the blank Watch & Shop area. Cards stay visible always.
    */
    if (cards.length) {
      gsap.killTweensOf(cards);
      gsap.set(cards, { clearProps: 'opacity,transform,visibility' });
      gsap.set(cards, { opacity: 1, y: 0, visibility: 'visible' });
    }

    if (prefersReducedMotion() || isDesignMode()) {
      showFinalState(section);
      if (accent) finalizeAccent(accent);
      return;
    }

    if (heading) {
      gsap.set(heading, { opacity: 0, y: 20 });
      if (accent) prepareAccent(accent);

      var titleTl = gsap.timeline({
        scrollTrigger: {
          trigger: heading,
          start: 'top 90%',
          once: true,
          invalidateOnRefresh: true,
        },
      });
      titleTl.to(heading, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, 0);
      if (accent) titleTl.add(buildAccentTimeline(accent), 0.35);
      if (divider) {
        gsap.set(divider, { opacity: 0 });
        titleTl.to(divider, { opacity: 1, duration: 0.4, ease: 'power2.out' }, 0.25);
      }
    }
  }

  /* ─── Community signup (footer) ─── */

  function initCommunitySignup(root) {
    var section = getSection(SECTION_KEYS.communitySignup, root);
    if (!section || !markInit(section)) return;

    var heading = section.querySelector('.yh-community__heading');
    var eyebrow = section.querySelector('.yh-community__eyebrow');
    var subheading = section.querySelector('.yh-community__subheading');
    var field = section.querySelector('.yh-community__field');
    var submit = section.querySelector('.yh-community__submit');
    var accent = wrapAccentWord(heading, accentWordForSection(section) || 'Spice');

    if (prefersReducedMotion() || isDesignMode()) {
      showFinalState(section);
      if (accent) finalizeAccent(accent);
      return;
    }

    if (eyebrow) gsap.set(eyebrow, { opacity: 0, y: 12 });
    if (heading) gsap.set(heading, { opacity: 0, y: 20 });
    if (subheading) gsap.set(subheading, { opacity: 0, y: 12 });
    if (field) gsap.set(field, { opacity: 0, y: 16 });
    if (submit) gsap.set(submit, { opacity: 0, y: 16 });
    if (accent) prepareAccent(accent);

    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top 90%',
        once: true,
        invalidateOnRefresh: true,
      },
    });
    if (eyebrow) tl.to(eyebrow, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' }, 0);
    if (heading) tl.to(heading, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, 0.05);
    if (accent) tl.add(buildAccentTimeline(accent), 0.35);
    if (subheading) tl.to(subheading, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 0.22);
    if (field) tl.to(field, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 0.32);
    if (submit) tl.to(submit, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 0.38);
  }

  /* ─── Sourcing journey / How It's Made ─── */

  function initSourcingJourney(root) {
    var section = getSection(SECTION_KEYS.sourcingJourney, root);
    if (!section || !markInit(section)) return;

    var heading = section.querySelector('.yh-sourcing__heading');
    var eyebrow = section.querySelector('.yh-sourcing__eyebrow');
    var subheading = section.querySelector('.yh-sourcing__subheading');
    var steps = gsap.utils.toArray(section.querySelectorAll('.yh-sourcing__step'));
    var accent = wrapAccentWord(heading, accentWordForSection(section) || 'Kitchen');

    /*
      Never animate step opacity. Same blank-area bug as Watch & Shop reels —
      ScrollTrigger.batch can miss and leave steps at opacity:0.
    */
    if (steps.length) {
      gsap.killTweensOf(steps);
      gsap.set(steps, { clearProps: 'opacity,transform,visibility' });
      gsap.set(steps, { opacity: 1, y: 0, visibility: 'visible' });
    }

    if (prefersReducedMotion() || isDesignMode()) {
      showFinalState(section);
      if (accent) finalizeAccent(accent);
      return;
    }

    if (eyebrow) gsap.set(eyebrow, { opacity: 0, y: 12 });
    if (heading) gsap.set(heading, { opacity: 0, y: 20 });
    if (subheading) gsap.set(subheading, { opacity: 0, y: 12 });
    if (accent) prepareAccent(accent);

    var titleTl = gsap.timeline({
      scrollTrigger: {
        trigger: heading || section,
        start: 'top 90%',
        once: true,
        invalidateOnRefresh: true,
      },
    });
    if (eyebrow) titleTl.to(eyebrow, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' }, 0);
    if (heading) titleTl.to(heading, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, 0.05);
    if (accent) titleTl.add(buildAccentTimeline(accent), 0.35);
    if (subheading) titleTl.to(subheading, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 0.22);
  }

  /* ─── Public init ─── */

  function initSectionById(fullSectionId, root) {
    var key = sectionKeyFromId(fullSectionId);
    switch (key) {
      case SECTION_KEYS.slideshow:
        initHero(root);
        break;
      case SECTION_KEYS.flashSale:
        initFlashSale(root);
        break;
      case SECTION_KEYS.featuredA:
      case SECTION_KEYS.featuredB:
        initFeaturedCollection(key, root);
        break;
      case SECTION_KEYS.richText:
        initRichText(root);
        break;
      case SECTION_KEYS.collectionList:
        initCollectionList(root);
        break;
      case SECTION_KEYS.discover:
        initDiscover(root);
        break;
      case SECTION_KEYS.shopByVideo:
        initShopByVideo(root);
        break;
      case SECTION_KEYS.communitySignup:
        initCommunitySignup(root);
        break;
      case SECTION_KEYS.sourcingJourney:
        initSourcingJourney(root);
        break;
      default:
        break;
    }
  }

  function initHomepageAnimations(root) {
    var scope = root && root.nodeType === 1 ? root : document;

    if (prefersReducedMotion() || isDesignMode()) {
      Object.keys(SECTION_KEYS).forEach(function (name) {
        var section = getSection(SECTION_KEYS[name], scope);
        if (!section) return;
        killSectionAnimations(section);
        section.querySelectorAll(HEADING_SEL).forEach(function (h) {
          wrapAccentWord(h, accentWordForSection(section));
        });
        showFinalState(section);
        section.setAttribute(ATTR_INIT, 'true');
      });
      return;
    }

    if (scope.id && scope.id.indexOf('shopify-section-') === 0) {
      initSectionById(scope.id, scope);
      return;
    }

    initHero(scope);
    initFlashSale(scope);
    initFeaturedCollection(SECTION_KEYS.featuredA, scope);
    initFeaturedCollection(SECTION_KEYS.featuredB, scope);
    initRichText(scope);
    initCollectionList(scope);
    initDiscover(scope);
    initShopByVideo(scope);
    initCommunitySignup(scope);
    initSourcingJourney(scope);
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(function () {
    initHomepageAnimations(document);
    requestAnimationFrame(refreshAndUnstick);
  });

  window.addEventListener('load', function () {
    refreshAndUnstick();
    setTimeout(refreshAndUnstick, 400);
    setTimeout(refreshAndUnstick, 1200);
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refreshAndUnstick();
  });

  var scrollTick = false;
  window.addEventListener(
    'scroll',
    function () {
      if (scrollTick) return;
      scrollTick = true;
      requestAnimationFrame(function () {
        revealStuckInView();
        scrollTick = false;
      });
    },
    { passive: true }
  );

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    if (!section || !section.id || section.id.indexOf('shopify-section-') !== 0) return;
    killSectionAnimations(section);
    if (prefersReducedMotion() || isDesignMode()) {
      section.querySelectorAll(HEADING_SEL).forEach(function (h) {
        wrapAccentWord(h, accentWordForSection(section));
      });
      showFinalState(section);
      section.setAttribute(ATTR_INIT, 'true');
      return;
    }
    initHomepageAnimations(section);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target;
    if (!section || !section.id) return;
    killSectionAnimations(section);
  });

  document.addEventListener('shopify:section:reorder', function () {
    Object.keys(SECTION_KEYS).forEach(function (name) {
      var section = getSection(SECTION_KEYS[name], document);
      if (section) killSectionAnimations(section);
    });
    initHomepageAnimations(document);
  });

  window.initHomepageAnimations = initHomepageAnimations;
})();
